import { api } from '@/core/http/apiClient';
import { exportFile } from '@/core/http/helpers';
import type {
  ChartTemplateAccountRow,
  ChartTemplateExportQuery,
  ChartTemplateOptions,
  ChartTemplateQuery,
  EmailTemplateRow,
  FirmAccountantOptions,
  FirmAccountantRow,
  FirmDataSecurityResponse,
  FirmNoteTextRow,
  LegacyAccountCodeRow,
  ClientReportFormatResponse,
  ReportFormatResponse,
  ReportFormatSettings,
  ReportLogoInfo,
  ReportNotesKey,
  ReportNotesSettings,
  ReportNotesSettingsResponse,
  SaveChartTemplateAccountPayload,
  SaveFirmAccountantPayload,
} from '@/core/models/firm-settings.models';

/** Typed client for the firm-wide Settings endpoints (`api/v1/FirmSettings`). */

const base = 'FirmSettings';

let chartOptions: Promise<ChartTemplateOptions> | undefined;

const toReportFormatResponse = (response: ClientReportFormatResponse): ReportFormatResponse => ({
  settings: response.settings,
  options: response.options,
  isCustomised: response.isClientSpecific,
  updatedAtUtc: response.updatedAtUtc,
});

export const firmSettingsService = {
  // ---- Accountants ----

  getAccountantOptions: () => api.get<FirmAccountantOptions>(`${base}/getAccountantOptions`),

  getAccountants: (search: string | null) => api.post<FirmAccountantRow[]>(`${base}/getAccountants`, { search }),

  getAccountant: (id: string) => api.get<FirmAccountantRow>(`${base}/getAccountant/${id}`),

  saveAccountant: (payload: SaveFirmAccountantPayload) =>
    api.post<FirmAccountantRow>(`${base}/saveAccountant`, payload),

  deleteAccountants: (ids: string[]) => api.post<boolean>(`${base}/deleteAccountants`, ids),

  // ---- Default chart of accounts ----

  /** Options change only on deploy, so the four chart tabs share one request (cleared if it fails). */
  getChartTemplateOptions: (): Promise<ChartTemplateOptions> => {
    if (!chartOptions) {
      const request = api.get<ChartTemplateOptions>(`${base}/getChartTemplateOptions`);
      chartOptions = request;
      request.catch(() => {
        if (chartOptions === request) chartOptions = undefined;
      });
    }
    return chartOptions;
  },

  getChartTemplate: (businessType: number, query: ChartTemplateQuery) =>
    api.post<ChartTemplateAccountRow[]>(`${base}/getChartTemplate/${businessType}`, query),

  /**
   * BUG-011: the old Capium codes of a business type (0 limited, 1 sole trader, 2 partnership, 3 LLP) matching `search` (old
   * code, old name or current code) and the current account each one maps to, from `GET ChartOfAccounts/legacyCodes`.
   */
  getLegacyAccountCodes: (businessType: number, search: string) =>
    api.get<LegacyAccountCodeRow[]>(
      `ChartOfAccounts/legacyCodes?clientType=${businessType}&search=${encodeURIComponent(search)}`,
    ),

  getChartTemplateAccount: (id: string) => api.get<ChartTemplateAccountRow>(`${base}/getChartTemplateAccount/${id}`),

  saveChartTemplateAccount: (payload: SaveChartTemplateAccountPayload) =>
    api.post<ChartTemplateAccountRow[]>(`${base}/saveChartTemplateAccount`, payload),

  archiveChartTemplateAccount: (id: string, value: boolean) =>
    api.post<ChartTemplateAccountRow>(`${base}/archiveChartTemplateAccount/${id}`, { value }),

  deleteChartTemplateAccounts: (ids: string[]) => api.post<boolean>(`${base}/deleteChartTemplateAccounts`, ids),

  exportChartTemplate: (businessType: number, query: ChartTemplateExportQuery) =>
    exportFile(`${base}/exportChartTemplate/${businessType}`, query, !!query.isPrint),

  // ---- Report notes ----

  getReportNotes: (key: ReportNotesKey) =>
    api.get<ReportNotesSettingsResponse>(`${base}/getReportNotesSettings/${key}`),

  saveReportNotes: (key: ReportNotesKey, settings: ReportNotesSettings) =>
    api.post<ReportNotesSettingsResponse>(`${base}/saveReportNotesSettings/${key}`, settings),

  // ---- Default note texts per layout (BUG-001) ----

  getNoteTexts: (key: string) => api.get<FirmNoteTextRow[]>(`${base}/getNoteTexts/${key}`),

  saveNoteText: (key: string, section: number, noteKey: string, content: string) =>
    api.post<FirmNoteTextRow[]>(`${base}/saveNoteText/${key}`, { section, noteKey, content }),

  resetNoteText: (key: string, section: number, noteKey: string) =>
    api.post<FirmNoteTextRow[]>(`${base}/resetNoteText/${key}/${section}/${encodeURIComponent(noteKey)}`, {}),

  // ---- Report format ----

  getReportFormat: () => api.get<ReportFormatResponse>(`${base}/getReportFormat`),

  saveReportFormat: (settings: ReportFormatSettings) =>
    api.post<ReportFormatResponse>(`${base}/saveReportFormat`, settings),

  // BUG-074: a client's own Report Format. The API says `isClientSpecific`; the page reads it as `isCustomised`.

  getClientReportFormat: (clientId: string) =>
    api.get<ClientReportFormatResponse>(`${base}/getClientReportFormat/${clientId}`).then(toReportFormatResponse),

  saveClientReportFormat: (clientId: string, settings: ReportFormatSettings) =>
    api
      .post<ClientReportFormatResponse>(`${base}/saveClientReportFormat/${clientId}`, settings)
      .then(toReportFormatResponse),

  resetClientReportFormat: (clientId: string) =>
    api
      .post<ClientReportFormatResponse>(`${base}/resetClientReportFormat/${clientId}`, {})
      .then(toReportFormatResponse),

  resetReportFormat: () => api.post<ReportFormatResponse>(`${base}/resetReportFormat`, {}),

  // ---- Report logo ----

  getReportLogo: () => api.get<ReportLogoInfo>(`${base}/getReportLogo`),

  getReportLogoImage: () => api.get<Blob>(`${base}/getReportLogoImage`, { responseType: 'blob' }),

  uploadReportLogo: (file: File) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return api.post<ReportLogoInfo>(`${base}/uploadReportLogo`, formData);
  },

  setReportLogoEnabled: (value: boolean) => api.post<ReportLogoInfo>(`${base}/setReportLogoEnabled`, { value }),

  deleteReportLogo: () => api.post<ReportLogoInfo>(`${base}/deleteReportLogo`, {}),

  // ---- Data security ----

  getDataSecurity: () => api.get<FirmDataSecurityResponse>(`${base}/getDataSecurity`),

  setDataSecurityEnabled: (value: boolean) =>
    api.post<FirmDataSecurityResponse>(`${base}/setDataSecurityEnabled`, { value }),

  // ---- Email templates ----

  getEmailTemplates: () => api.get<EmailTemplateRow[]>(`${base}/getEmailTemplates`),

  saveEmailTemplate: (key: string, subject: string, body: string) =>
    api.post<EmailTemplateRow>(`${base}/saveEmailTemplate`, { key, subject, body }),

  resetEmailTemplate: (key: string) =>
    api.post<EmailTemplateRow>(`${base}/resetEmailTemplate/${encodeURIComponent(key)}`, {}),
};
